import { kindByResource } from "@nodus/api";
import { ActionLink, EmptyState, Page } from "@nodus/ui";
import { createFileRoute, Outlet, redirect, useLocation, useParams } from "@tanstack/react-router";

import * as React from "react";
import { homePath } from "../../app/home";
import { ORG_SEGMENTS } from "../../app/nav";
import { projectIn } from "../../app/scope";
import { CATALOG_PROJECT } from "../../features/training/model";

// Catalog objects live in the `nodus` project, which no org lists; only their detail pages are served under it.
const CATALOG_DETAIL = new Set(["images", "environments", "trainingruntimes", "models"]);

// `/$org/$project`: every query key under it starts with [org, project] (ui-spec §4.18). A project the org does not
// have redirects to the same page in its default project, so a stale link or last-used project never shows as empty.
export const Route = createFileRoute("/_authed/$org/$project")({
  loader: async ({ context, params, location }) => {
    // Org-level sections (billing, settings…) whose routes are not registered yet land here as a "project".
    if (ORG_SEGMENTS.has(params.project)) return { known: true, orgSection: true };
    const parts = location.pathname.split("/");
    if (params.project === CATALOG_PROJECT && CATALOG_DETAIL.has(parts[3] ?? "") && parts[4]) return { known: true, orgSection: false };
    const fetchProjects = (staleTime?: number) =>
      context.queryClient.fetchQuery({ queryKey: [params.org, "", "projects"], queryFn: () => context.api.projects(params.org), ...(staleTime === undefined ? {} : { staleTime }) });
    let projects = (await fetchProjects()).map((p) => p.metadata.name);
    // A project created since the list was cached is checked against a fresh list before it counts as unknown.
    if (!projects.includes(params.project)) projects = (await fetchProjects(0)).map((p) => p.metadata.name);
    if (projects.includes(params.project)) {
      return { known: true, orgSection: false };
    }
    const fallback = projectIn(projects);
    if (fallback !== params.project && projects.includes(fallback)) {
      parts[2] = encodeURIComponent(fallback);
      throw redirect({ href: `${parts.join("/")}${location.searchStr}${location.hash ? `#${location.hash}` : ""}`, replace: true });
    }
    return { known: false, orgSection: false };
  },
  component: ProjectLayout,
  notFoundComponent: ProjectNotFound,
});

function ProjectLayout() {
  const { known, orgSection } = Route.useLoaderData();
  const { org, project } = useParams({ strict: false }) as { org: string; project: string };
  if (orgSection) return <ProjectNotFound />;
  if (!known) {
    return (
      <Page>
        <EmptyState
          title={`No project named ${project}`}
          description={`Organization ${org} has no project with that name.`}
          actions={<ActionLink href={homePath(org, "default")}>Open the default project</ActionLink>}
        />
      </Page>
    );
  }
  return <Outlet />;
}

/** A section whose product pages are not merged yet reads as "coming", not as a broken link. */
function ProjectNotFound() {
  const { pathname } = useLocation();
  const { org, project } = useParams({ strict: false }) as { org: string; project: string };
  const parts = pathname.split("/").filter(Boolean);
  const segment = parts[2] ?? "";
  const kind = ORG_SEGMENTS.has(parts[1] ?? "") || kindByResource(segment) || ["runs", "inference", "pools"].includes(segment);
  React.useEffect(() => {
    document.title = "Not available · Nodus";
  }, []);
  return (
    <Page>
      <EmptyState
        title={kind ? "This section is not available yet" : "Page not found"}
        description={kind ? "The CLI and API already work." : undefined}
        actions={<ActionLink href={homePath(org, project)}>Back to Runs</ActionLink>}
      />
    </Page>
  );
}
